'use client';

/**
 * Canonical client-side accessor for the active tenant.
 *
 * Replaces the older `useActiveTenantId` (cookie reader) and the various
 * ad-hoc `localStorage.getItem('currentTenantId')` calls scattered across
 * the admin. Those all trusted client-owned storage, which created the
 * classic "previous user's tenant leaks into next user's session" bug.
 *
 * Contract:
 *   - On mount, fetches `GET /api/admin/active-tenant?candidate=<hint>`.
 *     The server validates the hint against the user's roles and returns
 *     the canonical answer. The hint is the *last value* the cookie /
 *     localStorage had — never trusted, only suggested.
 *   - The cookie + localStorage are then rewritten to the canonical value.
 *     That way the next page-load's hint is already correct (free perf
 *     win) without ever depending on the client value for correctness.
 *
 * Provider model (`ActiveTenantProvider`) means the fetch happens once at
 * the layout root, not per component. Children use `useActiveTenant()`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { isHotReloadDisabled } from '@/lib/admin/rbacFlags';

const TENANT_COOKIE_TTL_DAYS = 30;

export interface AvailableTenant {
  id: string;
  name: string;
  slug: string | null;
}

export interface ActiveTenant {
  tenantId: string | null;
  name: string | null;
  slug: string | null;
  /** Verified custom domain (e.g. `buchen.example.de`) if the tenant
   *  has one, else null. Used by the Booking-Widget snippet generator
   *  to prefer the customer's branded URL over `eazybooking.de/<slug>`. */
  primaryDomain: string | null;
  /** When the owner has scheduled their account for deletion (DSGVO
   *  Art. 17), this is the ISO timestamp of the planned hard-delete.
   *  Drives the Danger-Zone banner in `/admin/settings/account`. */
  deletionScheduledAt: string | null;
  role: string | null;
  /** `tenant_user_roles.role_id` for the active membership. Used by
   *  audit-log writers + RBAC shim. */
  roleId: string | null;
  /** Permission slugs granted to this user in the active tenant.
   *  Super-admins / admins get `['*']` as a wildcard short-circuit. */
  permissions: string[];
  /** Convenience flag — same as `permissions.includes('*')`. */
  isSuperAdmin: boolean;
  primary: string | null;
  isViewAs: boolean;
  isPlatformAdmin: boolean;
  available: AvailableTenant[];
  /** Tenant display currency (ISO 4217). Drives every money formatter
   *  in the admin UI via `useAdminFormatters()`. Defaults to 'EUR'
   *  when the tenant has never set a value. */
  currency: string;
  /** Tenant default UI locale (BCP 47 — 'de', 'en', 'fr', …). Drives
   *  every locale-aware formatter (date/number/percent). Defaults
   *  to 'de'. */
  defaultLocale: string;
  /** IANA tenant timezone — single source of truth for date/time
   *  display in admin tables, charts, and dashboard widgets. Defaults
   *  to 'Europe/Berlin'. */
  timezone: string;
  /** Calendar-grid week-start convention: 0=Sunday (US/CA/MX),
   *  1=Monday (DACH/EU). Drives the storefront DateSelection grid +
   *  any admin calendar widget. Defaults to 1 (Monday). */
  firstDayOfWeek: 0 | 1;
}

interface ActiveTenantContextValue extends ActiveTenant {
  loading: boolean;
  error: string | null;
  /** Trigger a re-fetch (e.g. after the user switches tenant). */
  refresh: () => Promise<void>;
  /** Switch active tenant by suggesting a candidate — server validates. */
  switchTo: (candidateId: string) => Promise<void>;
}

const EMPTY: ActiveTenant = {
  tenantId: null,
  name: null,
  slug: null,
  primaryDomain: null,
  deletionScheduledAt: null,
  role: null,
  roleId: null,
  permissions: [],
  isSuperAdmin: false,
  primary: null,
  isViewAs: false,
  isPlatformAdmin: false,
  available: [],
  currency: 'EUR',
  defaultLocale: 'de',
  timezone: 'Europe/Berlin',
  firstDayOfWeek: 1,
};

const ActiveTenantContext = createContext<ActiveTenantContextValue | null>(null);

/**
 * The tenant_id cookie is now `HttpOnly` — the browser sends it
 * automatically on every request but JS cannot read or write it
 * anymore. So the client has no "hint" to pass; the server reads
 * the cookie itself in the resolver. This function exists only as
 * a backwards-compat stub for callers that still ask for a hint
 * (they'll get null and the server falls through to its cookie).
 */
function readClientHint(): string | null {
  return null;
}

/**
 * No-op: the resolver writes the canonical cookie server-side with
 * HttpOnly + Secure + SameSite=Lax. The client no longer touches
 * `document.cookie` for `tenant_id` — that was the XSS-theft vector
 * we wanted to close.
 *
 * Kept as an empty function so call-sites don't break during the
 * transition; remove in a follow-up sweep once all callers are gone.
 */
function persistClientHint(_tenantId: string) {
  void _tenantId;
}

// Module-scope in-flight promise dedup. React 19 strict-mode in dev
// double-fires the mount effect, which without this guard meant two
// `/api/admin/active-tenant` requests per page-load (~2s wasted in
// dev, ~0 in prod but the dedup is correctness-cheap either way).
// Keyed by candidate so an explicit `switchTo()` still re-fetches.
let inflightActiveTenant: { key: string; promise: Promise<Response> } | null = null;
/**
 * Exported so non-context consumers (e.g. RBACProvider which sits one
 * level *outside* ActiveTenantProvider in the layout tree) can hit the
 * same canonical resolver without redundantly reading client storage
 * themselves. Stale localStorage tenant hints used to make rbac.tsx
 * race against this resolver and occasionally hang the admin spinner.
 */
export function dedupedActiveTenantFetch(candidate: string | null): Promise<Response> {
  const key = candidate ?? '';
  if (inflightActiveTenant && inflightActiveTenant.key === key) {
    return inflightActiveTenant.promise.then((r) => r.clone());
  }
  const url = candidate
    ? `/api/admin/active-tenant?candidate=${encodeURIComponent(candidate)}`
    : '/api/admin/active-tenant';
  const promise = fetch(url, { credentials: 'include' }).finally(() => {
    if (inflightActiveTenant?.key === key) inflightActiveTenant = null;
  });
  inflightActiveTenant = { key, promise };
  return promise.then((r) => r.clone());
}

export function ActiveTenantProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ActiveTenant>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActive = useCallback(async (candidate?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const res = await dedupedActiveTenantFetch(candidate ?? null);
      if (!res.ok) {
        // 401 / 404 — auth or no-tenant. Leave state empty; the caller's
        // layout will show login redirect / onboarding UI.
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setError(data.error ?? `HTTP ${res.status}`);
        setState(EMPTY);
        return;
      }
      const data = (await res.json()) as ActiveTenant;
      setState(data);
      if (data.tenantId) persistClientHint(data.tenantId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown');
      setState(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mount: send the current cookie/storage value as a hint. Server returns
  // the canonical answer, which we re-persist.
  useEffect(() => {
    void fetchActive(readClientHint());
  }, [fetchActive]);

  // Permission Hot-Reload — refetch on three triggers:
  //   - window focus (user comes back to the tab after some absence)
  //   - 5min interval poll (catches admin-side permission revokes)
  //   - BroadcastChannel message from a sibling tab that just refetched
  //
  // BroadcastChannel optimisation: only ONE tab per origin runs the
  // 5min poll (the "leader"). The leader broadcasts the fresh state
  // to follower tabs via `tenant-sync`, so 10 open admin tabs cost
  // one network round-trip every 5min instead of ten. Leader election
  // is implicit: each tab claims the role on mount and yields on
  // unmount; if no leader replies to a ping within 300ms, the asker
  // becomes the new leader. Simple, no Lock-API requirement (which
  // would need HTTPS + recent-browsers only).
  useEffect(() => {
    // Kill-switch: if NEXT_PUBLIC_RBAC_HOTRELOAD_DISABLED is set we
    // skip the whole hot-reload setup. Use when a regression appears
    // in production and you need to rule out the polling/broadcast
    // path. Tabs will only refresh on hard-reload until the flag is
    // cleared and a redeploy happens (bundle-level env-var).
    if (isHotReloadDisabled()) return;

    const applyState = (data: ActiveTenant) => {
      setState(data);
    };

    const silentRefetch = () => {
      // Skip when the tab is hidden — no point refetching (and burning
      // function CPU) for a backgrounded tab; the visibility listener
      // refreshes on return.
      if (typeof document !== 'undefined' && document.hidden) return;
      // Don't trigger loading flicker — just background-refresh the
      // canonical state. If the response changes, React re-renders.
      void (async () => {
        try {
          const res = await dedupedActiveTenantFetch(readClientHint());
          if (!res.ok) return;
          const data = (await res.json()) as ActiveTenant;
          applyState(data);
          // Tell sibling tabs about the fresh state so they don't
          // also fire their own request a few seconds later.
          broadcast?.postMessage({ type: 'state', data });
        } catch {
          // Silent — keep previous state on transient errors.
        }
      })();
    };

    // BroadcastChannel may not exist on very old browsers / SSR; fall
    // back to per-tab polling when not available.
    const broadcast =
      typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('tenant-sync') : null;

    // Leader-election state. `leaderProbeAck` tracks whether someone
    // else answered our `leader?` ping; if false after the timeout,
    // we promote ourselves.
    let isLeader = false;
    let leaderProbeAck = false;
    let intervalId: number | null = null;

    const startLeaderPoll = () => {
      if (intervalId !== null) return;
      isLeader = true;
      intervalId = window.setInterval(silentRefetch, 5 * 60_000);
    };

    const stopLeaderPoll = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      isLeader = false;
    };

    if (broadcast) {
      broadcast.onmessage = (e) => {
        const msg = e.data as
          | { type: 'state'; data: ActiveTenant }
          | { type: 'leader?' }
          | { type: 'leader!' };
        if (msg.type === 'state') {
          applyState(msg.data);
        } else if (msg.type === 'leader?' && isLeader) {
          broadcast.postMessage({ type: 'leader!' });
        } else if (msg.type === 'leader!') {
          leaderProbeAck = true;
          // Another tab is leader — make sure we're a follower.
          stopLeaderPoll();
        }
      };
      // Probe for an existing leader. If none answers within 300ms,
      // promote self.
      broadcast.postMessage({ type: 'leader?' });
      window.setTimeout(() => {
        if (!leaderProbeAck) startLeaderPoll();
      }, 300);
    } else {
      // No BroadcastChannel → every tab polls on its own.
      startLeaderPoll();
    }

    // Throttle the focus/visibility refetch. Without this, every single
    // tab-switch fired a fresh /api/admin/active-tenant request → felt like
    // "loading again" each time you came back. The server cache TTL is 30s,
    // so refetching more often than that can't return anything newer anyway.
    let lastEdgeRefetch = 0;
    const EDGE_REFETCH_THROTTLE_MS = 30_000;
    const edgeRefetch = () => {
      if (typeof document === 'undefined' || document.hidden) return;
      const now = Date.now();
      if (now - lastEdgeRefetch < EDGE_REFETCH_THROTTLE_MS) return;
      lastEdgeRefetch = now;
      silentRefetch();
    };

    window.addEventListener('focus', edgeRefetch);
    document.addEventListener('visibilitychange', edgeRefetch);

    return () => {
      window.removeEventListener('focus', edgeRefetch);
      document.removeEventListener('visibilitychange', edgeRefetch);
      stopLeaderPoll();
      broadcast?.close();
    };
  }, []);

  const refresh = useCallback(() => fetchActive(readClientHint()), [fetchActive]);
  const switchTo = useCallback(
    (candidateId: string) => fetchActive(candidateId),
    [fetchActive]
  );

  const value = useMemo<ActiveTenantContextValue>(
    () => ({ ...state, loading, error, refresh, switchTo }),
    [state, loading, error, refresh, switchTo]
  );

  return (
    <ActiveTenantContext.Provider value={value}>
      {children}
    </ActiveTenantContext.Provider>
  );
}

export function useActiveTenant(): ActiveTenantContextValue {
  const ctx = useContext(ActiveTenantContext);
  if (!ctx) {
    throw new Error('useActiveTenant must be used within ActiveTenantProvider');
  }
  return ctx;
}
